import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { api, errorMessage } from "../../lib/api";
import { EVENT_TYPES, SPEAKING_TOPICS } from "../../data/content";
import { Field, TextInput, TextArea, Choice, Honeypot, FormStatus } from "./Fields";

const EMPTY = { name: "", email: "", organization: "", event_type: "", event_name: "", topics: [], format: "Not sure yet", location: "", dates: "", audience: "", details: "", website: "" };

export const SpeakingForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggle = (t) => setForm((f) => ({ ...f, topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_type) {
      setStatus({ type: "error", title: "A little more detail, please", text: "Choose the type of event." });
      return;
    }
    setBusy(true); setStatus(null);
    try {
      await api.post("/speaking", form);
      setStatus({ type: "success", title: "Thank you, your invitation was sent.", text: `Michelle will reply to ${form.email} about availability and details.` });
      toast.success("Speaking invitation sent");
      setForm(EMPTY);
    } catch (err) {
      const msg = errorMessage(err);
      setStatus({ type: "error", title: "Your invitation wasn't sent", text: msg });
      toast.error("Invitation not sent", { description: msg });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} aria-label="Speaking invitation" className="space-y-6" data-testid="speaking-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="sp-name" label="Name" required><TextInput id="sp-name" autoComplete="name" required value={form.name} onChange={set("name")} /></Field>
        <Field id="sp-email" label="Email" required><TextInput id="sp-email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} /></Field>
        <Field id="sp-organization" label="Organization" required><TextInput id="sp-organization" autoComplete="organization" required value={form.organization} onChange={set("organization")} /></Field>
        <Field id="sp-event-type" label="Type of event" required><Choice id="sp-event-type" value={form.event_type} onChange={set("event_type")} options={EVENT_TYPES} /></Field>
        <Field id="sp-event-name" label="Event name"><TextInput id="sp-event-name" value={form.event_name} onChange={set("event_name")} /></Field>
        <Field id="sp-format" label="Format" required><Choice id="sp-format" value={form.format} onChange={set("format")} options={["In person", "Virtual", "Not sure yet"]} /></Field>
        <Field id="sp-location" label="Location"><TextInput id="sp-location" value={form.location} onChange={set("location")} /></Field>
        <Field id="sp-dates" label="Date or dates"><TextInput id="sp-dates" value={form.dates} onChange={set("dates")} /></Field>
        <Field id="sp-audience" label="Audience and size" hint="e.g. 40 teachers of the Deaf"><TextInput id="sp-audience" value={form.audience} onChange={set("audience")} aria-describedby="sp-audience-hint" /></Field>
      </div>
      <fieldset>
        <legend className="lbl text-ink">Topics of interest <span className="normal-case tracking-normal font-normal text-slate">(optional)</span></legend>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 font-sans text-[15px]">
          {SPEAKING_TOPICS.map((t, i) => (
            <div key={t.title} className="flex items-start gap-3">
              <Checkbox id={`sp-topic-${i}`} checked={form.topics.includes(t.title)} onCheckedChange={() => toggle(t.title)} className="mt-0.5 border-ink data-[state=checked]:bg-teal data-[state=checked]:border-teal" data-testid={`speaking-topic-checkbox-${i}`} />
              <label htmlFor={`sp-topic-${i}`} className="cursor-pointer">{t.title}</label>
            </div>
          ))}
        </div>
      </fieldset>
      <Field id="sp-details" label="Anything else"><TextArea id="sp-details" rows={3} value={form.details} onChange={set("details")} /></Field>
      <Honeypot value={form.website} onChange={set("website")} />
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 font-sans bg-teal text-paper rounded-full px-8 py-4 hover:bg-teal-deep transition-colors disabled:opacity-70" data-testid="speaking-submit-button">
        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}{busy ? "Sending..." : "Invite Michelle to speak"}
      </button>
      <FormStatus status={status} testid="speaking-form-status" />
    </form>
  );
};
